import { computed } from 'vue'
import { useUserStore } from '@/config/store/userStore'
import { RecruitmentScope } from '@/services/resource'

export type ScopeOffice = {
  _id: string
  display: string
  countryId: string
}

export type ScopeCountryGroup = {
  _id: string
  display: string
  items: ScopeOffice[]
}

// Ids arrive as strings or as populated documents
export const toId = (value: unknown): string => {
  if (value == null || value === '')
    return ''
  if (typeof value === 'object')
    return toId((value as { _id?: unknown })._id)
  return String(value)
}

const byDisplay = (a: { display: string }, b: { display: string }) => a.display.localeCompare(b.display)

/**
 * Offices the connected recruiter covers, mirroring the API (profile/recruitmentOfficeScope.js):
 * GLOBAL every office, COUNTRY the offices of their office's country, OFFICEGROUP the attached
 * offices, OFFICE and MANAGED their own office.
 */
export default function useRecruitmentOfficeScope() {
  const userStore = useUserStore()

  const recruitment = computed(() => userStore.user?.profile?.modules?.recruitment)
  const scope = computed(() => recruitment.value?.recruitmentOverview || RecruitmentScope.MANAGED)
  const userOfficeId = computed(() => toId(userStore.user?.currentJob?.office) || toId(userStore.user?.currentJob?.officeId))
  const userCountryId = computed(() => toId(userStore.user?.currentJob?.office?.countryId))

  const firmOffices = computed(() => userStore.firmContext.offices || [])
  const firmCountries = computed(() => userStore.firmContext.countries || [])

  const findOffice = (officeId?: string | null) => firmOffices.value.find(office => office._id === officeId)
  const officeLabel = (officeId?: string | null) => (officeId && (findOffice(officeId)?.display || officeId)) || ''
  const officeCountryId = (officeId?: string | null) => findOffice(officeId)?.countryId || ''
  const countryLabel = (countryId?: string | null) =>
    (countryId && (firmCountries.value.find(country => country._id === countryId)?.display || countryId)) || ''

  const officesInScope = computed<ScopeOffice[]>(() => {
    const zoneIds = (recruitment.value?.recruitmentOverviewOfficeZoneIds || []).map(toId)
    const covered = firmOffices.value.filter((office) => {
      switch (scope.value) {
        case RecruitmentScope.GLOBAL:
          return true
        case RecruitmentScope.COUNTRY:
          return !!userCountryId.value && office.countryId === userCountryId.value
        case RecruitmentScope.OFFICEGROUP:
          return zoneIds.includes(office._id)
        default:
          return office._id === userOfficeId.value
      }
    })
    return covered.map(office => ({ _id: office._id, display: office.display || office._id, countryId: office.countryId || '' }))
  })

  const isOfficeInScope = (officeId?: string | null) => !!officeId && officesInScope.value.some(office => office._id === officeId)

  /** Offices in scope grouped by country, countries and offices sorted by name. */
  const officeGroups = computed<ScopeCountryGroup[]>(() => {
    const groups = new Map<string, ScopeCountryGroup>()
    for (const office of officesInScope.value) {
      const group = groups.get(office.countryId)
      if (group)
        group.items.push(office)
      else
        groups.set(office.countryId, { _id: office.countryId, display: countryLabel(office.countryId), items: [office] })
    }
    return [...groups.values()]
      .map(group => ({ ...group, items: [...group.items].sort(byDisplay) }))
      .sort(byDisplay)
  })

  return {
    scope,
    userCountryId,
    officesInScope,
    officeGroups,
    isOfficeInScope,
    findOffice,
    officeLabel,
    officeCountryId,
    countryLabel,
  }
}
